import { useCallback, useRef, useState, useContext } from 'react';

import { DialogError } from '../../../business-error';
import { useDialog } from '../../../dialog/useDialog';
import { ToastNotificationContext } from '../../../toast-notification';
import { handleResponseError } from '../../../../../api/handleResponseError';

import { getCancelReportBusinessError } from './bussinessErrors';
import { HandleCancelReportRequest, CanceledReport } from './interfaces';

interface Props {
  handleSuccess: () => void;
  handleCancelReportRequest: HandleCancelReportRequest;
}

export const useCancelReport = ({ handleSuccess, handleCancelReportRequest }: Props) => {
  const { openToast } = useContext(ToastNotificationContext);
  const [cancellingReport, setCancellingReport] = useState<CanceledReport>();
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [isReportCancelling, setLoading] = useState<boolean>(false);
  const [cancelReportError, setError] = useState<DialogError>(null);

  const handleCancelReport = useCallback(async () => {
    try {
      if (cancellingReport) {
        setLoading(true);
        setError(null);

        const response = await handleCancelReportRequest(cancellingReport.id);

        if (response.status !== 204) {
          await handleResponseError(response);
        }

        openToast({
          message: 'Report successfully cancelled',
          severity: 'success'
        });

        setLoading(false);
        handleCloseDialog();
        handleSuccess();
      }
    } catch (e) {
      const { message, severity } = getCancelReportBusinessError(e);

      setError({ message, severity });
      setLoading(false);
    }
  }, [cancellingReport, handleCancelReportRequest, handleCloseDialog, handleSuccess, openToast]);

  const handleOpenCancelDialog = useRef((report: CanceledReport) => {
    handleOpenDialog();
    setCancellingReport(report);
  });

  const handleCloseCancelDialog = () => {
    setError(null);
    handleCloseDialog();
  };

  return {
    handleOpenCancelDialog: handleOpenCancelDialog.current,
    openCancelDialog: isDialogOpen,
    handleCloseCancelDialog,
    handleCancelReport,
    isReportCancelling,
    cancellingReport,
    cancelReportError
  };
};
